import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, type LucideIcon, SearchX } from 'lucide-react';

import { cn } from '@/shared/libs';

import { Button } from './button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from './empty';

export type ListEmptyVariant = 'empty' | 'filtered';

export interface ListEmptyProps {
  actionLabel?: string;
  className?: string;
  description?: string;
  icon?: LucideIcon;
  onAction?: () => void;
  title?: string;
  variant?: ListEmptyVariant;
}

const VARIANT_ICON: Record<ListEmptyVariant, LucideIcon> = {
  empty: Inbox,
  filtered: SearchX,
};

export const ListEmpty: FC<ListEmptyProps> = ({
  actionLabel,
  className,
  description,
  icon,
  onAction,
  title,
  variant = 'empty',
}) => {
  const { t } = useTranslation();
  const Icon = icon ?? VARIANT_ICON[variant];

  const resolvedTitle =
    title ??
    (variant === 'filtered'
      ? t('listEmpty.filteredTitle')
      : t('listEmpty.emptyTitle'));
  const resolvedDescription =
    description ??
    (variant === 'filtered'
      ? t('listEmpty.filteredDescription')
      : t('listEmpty.emptyDescription'));
  const resolvedActionLabel =
    actionLabel ??
    (variant === 'filtered' ? t('listEmpty.clearFilters') : undefined);

  return (
    <Empty
      className={cn(
        'min-h-[280px] w-full rounded-xl border border-dashed border-border bg-muted/30 py-16',
        className,
      )}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon className="size-6 text-muted-foreground" />
        </EmptyMedia>
        <EmptyTitle>{resolvedTitle}</EmptyTitle>
        <EmptyDescription>{resolvedDescription}</EmptyDescription>
      </EmptyHeader>
      {!!onAction && !!resolvedActionLabel && (
        <EmptyContent>
          <Button size="sm" type="button" variant="outline" onClick={onAction}>
            {resolvedActionLabel}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
};

ListEmpty.displayName = 'ListEmpty';

export interface ListEmptyState {
  action?: ReactNode;
  description?: string;
  title?: string;
  variant?: ListEmptyVariant;
}
